import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  walletService,
  type AdminCoinAdjustmentRequest,
} from '@/services/wallet.service';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

interface AdjustBalanceModalProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  userName: string;
  currentBalance: number;
}

export function AdjustBalanceModal({
  open,
  onClose,
  userId,
  userName,
  currentBalance,
}: AdjustBalanceModalProps) {
  const queryClient = useQueryClient();
  const [operation, setOperation] = useState<'add' | 'subtract' | 'set'>('add');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const adjustMutation = useMutation({
    mutationFn: (request: AdminCoinAdjustmentRequest) =>
      walletService.adjustWalletBalance(request),
    onSuccess: (data) => {
      toast.success(
        `Balance adjusted successfully. New balance: ${data.newBalance}`,
      );
      queryClient.invalidateQueries({ queryKey: ['admin-wallets'] });
      queryClient.invalidateQueries({ queryKey: ['wallet-details', userId] });
      queryClient.invalidateQueries({
        queryKey: ['wallet-transactions', userId],
      });
      handleClose();
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Failed to adjust balance';
      toast.error(message);
    },
  });

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!amount || Number(amount) <= 0) {
      newErrors.amount = 'Amount must be greater than 0';
    }

    if (!reason || reason.trim().length < 10) {
      newErrors.reason = 'Reason must be at least 10 characters';
    }

    if (operation === 'set' && Number(amount) < 0) {
      newErrors.amount = 'Balance cannot be negative';
    }

    if (operation === 'subtract' && Number(amount) > currentBalance) {
      newErrors.amount = 'Cannot subtract more than current balance';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    let finalAmount = Number(amount);

    if (operation === 'subtract') {
      finalAmount = -finalAmount;
    } else if (operation === 'set') {
      finalAmount = finalAmount - currentBalance;
    }

    adjustMutation.mutate({
      userId,
      amount: finalAmount,
      description: reason,
      referenceType: 'AdminAdjustment',
    });
  };

  const handleClose = () => {
    setOperation('add');
    setAmount('');
    setReason('');
    setErrors({});
    onClose();
  };

  const calculateNewBalance = (): number => {
    const numAmount = Number(amount) || 0;

    if (operation === 'add') {
      return currentBalance + numAmount;
    } else if (operation === 'subtract') {
      return currentBalance - numAmount;
    } else {
      return numAmount;
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Adjust Wallet Balance</DialogTitle>
          <DialogDescription>
            Adjust balance for <strong>{userName}</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Current Balance */}
          <div className="p-3 bg-muted rounded-lg">
            <p className="text-sm text-muted-foreground">Current Balance</p>
            <p className="text-2xl font-semibold text-primary">
              {currentBalance.toLocaleString()}
            </p>
          </div>

          {/* Operation */}
          <div className="space-y-2">
            <Label htmlFor="operation">Operation</Label>
            <Select
              value={operation}
              onValueChange={(value) => {
                if (value === 'add' || value === 'subtract' || value === 'set') {
                  setOperation(value);
                }
              }}
            >
              <SelectTrigger id="operation">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="add">Add Coins</SelectItem>
                <SelectItem value="subtract">Subtract Coins</SelectItem>
                <SelectItem value="set">Set Balance</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Amount */}
          <div className="space-y-2">
            <Label htmlFor="amount">Amount</Label>
            <Input
              id="amount"
              type="number"
              placeholder="Enter amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              min="0"
            />
            {errors.amount && (
              <p className="text-sm text-danger">{errors.amount}</p>
            )}
          </div>

          {/* Preview New Balance */}
          {amount && (
            <div className="p-3 bg-success-light rounded-lg">
              <p className="text-sm text-muted-foreground">New Balance</p>
              <p className="text-2xl font-semibold text-success">
                {calculateNewBalance().toLocaleString()}
              </p>
            </div>
          )}

          {/* Reason */}
          <div className="space-y-2">
            <Label htmlFor="reason">Reason (required, min 10 characters)</Label>
            <Textarea
              id="reason"
              placeholder="Enter reason for adjustment..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
            />
            <p className="text-xs text-muted-foreground">
              {reason.length}/10 characters minimum
            </p>
            {errors.reason && (
              <p className="text-sm text-danger">{errors.reason}</p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={adjustMutation.isPending}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={adjustMutation.isPending}>
            {adjustMutation.isPending ? 'Adjusting...' : 'Confirm Adjustment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
